import { computed, inject } from '@angular/core'
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals'

import { Area } from '@models/area.model'
import { RealTimeSensorData } from '@models/sensorData.model'
import { Threshold } from '@models/threshold.model'
import { AreasService } from '@services/area.service'
import { SensorService } from '@services/sensor.service'
import { CompanyStore } from './company.store'

interface AreasState {
  areas: Area[]
  isLoading: boolean
  filter: { query: string; order: 'asc' | 'desc' }
  selectedArea: Area | null
}

const initialState: AreasState = {
  areas: [],
  isLoading: false,
  filter: { query: '', order: 'asc' },
  selectedArea: null,
}

export const AreasStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withState(initialState),

  withMethods(
    (
      store,
      areas$ = inject(AreasService),
      sensor$ = inject(SensorService),
    ) => ({
      loadAreasFromCompanies(companies: number[]): void {
        if (store.isLoading()) return
        patchState(store, {
          isLoading: true,
          areas: store
            .areas()
            .filter((a) => !companies.includes(a.companyId ?? 0)),
        })
        areas$.getAreasForCompanies(companies).subscribe({
          next: (areas) => {
            patchState(store, { areas: [...store.areas(), ...areas.flat()] })
          },
          error: console.error,
          complete: () => patchState(store, { isLoading: false }),
        })
      },
      setSelectedArea(area: Area | null): void {
        patchState(store, { selectedArea: area })
      },
      updateThreshold(sensorId: number, threshold: Threshold): Promise<void> {
        const areaId = store
          .areas()
          .find((area) =>
            area.devices.some((sensor) => sensor.id === sensorId),
          )!.id
        return new Promise((resolve, reject) => {
          sensor$.saveThreshold(areaId, sensorId, threshold).subscribe({
            next: (newThreshold) => {
              patchState(store, {
                areas: store.areas().map((area) => {
                  if (area.id === areaId) {
                    return {
                      ...area,
                      devices: area.devices.map((sensor) => {
                        if (sensor.id === sensorId) {
                          return { ...sensor, threshold: newThreshold }
                        }
                        return sensor
                      }),
                    }
                  }
                  return area
                }),
              })
              resolve()
            },
            error: (err) => {
              console.error(err)
              reject(err)
            },
          })
        })
      },
      updateAlerts(sensorId: number, alerts: Record<string, boolean>): Promise<void> {
        const areaId = store
          .areas()
          .find((area) =>
            area.devices.some((sensor) => sensor.id === sensorId),
          )!.id
        return new Promise((resolve, reject) => {
          sensor$.saveAlerts(areaId, sensorId, alerts).subscribe({
            next: () => {
              patchState(store, {
                areas: store.areas().map((area) => {
                  if (area.id !== areaId) return area
                  return {
                    ...area,
                    devices: area.devices.map((sensor) => {
                      if (sensor.id !== sensorId) return sensor
                      if (!sensor.threshold) return sensor
                      return {
                        ...sensor,
                        threshold: {
                          id: sensor.threshold.id,
                          thresholds: sensor.threshold.thresholds,
                          alerts,
                          mainSensor: sensor.threshold.mainSensor,
                        },
                      }
                    }),
                  }
                }),
              })
              resolve()
            },
            error: (err) => {
              console.error(err)
              reject(err)
            },
          })
        })
      },
      addDataToAreaDevice(areaDeviceId: number, data: RealTimeSensorData) {
        const normalizedData: RealTimeSensorData = {
          ...data,
          timeStamp:
            data.timeStamp instanceof Date
              ? data.timeStamp
              : new Date(data.timeStamp),
        }

        patchState(store, {
          areas: store.areas().map((area) => {
            const hasTargetDevice = area.devices.some(
              (device) => device.id === areaDeviceId,
            )

            if (!hasTargetDevice) {
              return area
            }

            return {
              ...area,
              devices: area.devices.map((device) =>
                device.id === areaDeviceId
                  ? {
                      ...device,
                      data: [normalizedData],
                    }
                  : device,
              ),
            }
          }),
        })
      },
      
      /**
       * Replace an existing area in the store with the provided object.
       * Used when an area has been edited (name, polygon, threshold etc).
       */
      updateArea(updated: Area) {
        patchState(store, {
          areas: store.areas().map((a) =>
            a.id === updated.id ? { ...a, ...updated } : a,
          ),
        })
      },
    }),
  ),
  withComputed((store, companies$ = inject(CompanyStore)) => ({
    areasForSelectedCompany: computed(() =>
      store
        .areas()
        .filter((area) =>
          companies$.selectedCompany()
            ? area.companyId === companies$.selectedCompany()?.id
            : false,
        ),
    ),
  })),
)
